import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowUpRight, Loader2, Monitor, RotateCw } from "lucide-react";

export const PreviewModal = ({ project, onClose }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!project) return;
    setLoaded(false);
    setFailed(false);
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    // If the embed hasn't loaded within 8s, treat it as blocked.
    const timer = setTimeout(() => {
      setLoaded((l) => {
        if (!l) setFailed(true);
        return l;
      });
    }, 8000);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      clearTimeout(timer);
    };
  }, [project, reloadKey, onClose]);

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          data-testid="preview-modal-overlay"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-2xl flex items-center justify-center p-3 md:p-8"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 8 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-7xl h-[88vh] bg-[#030303] rounded-2xl border border-white/10 overflow-hidden flex flex-col shadow-2xl"
          >
            {/* header */}
            <div className="flex items-center justify-between gap-4 px-4 md:px-5 py-3 border-b border-white/10 bg-[#0A0A0A]">
              <div className="flex items-center gap-3 min-w-0">
                <div className="hidden sm:flex gap-1.5 pr-2">
                  <span className="h-3 w-3 rounded-full bg-white/15" />
                  <span className="h-3 w-3 rounded-full bg-white/15" />
                  <span className="h-3 w-3 rounded-full bg-white/15" />
                </div>
                <div className="min-w-0">
                  <p className="font-[Outfit] text-sm font-semibold text-white truncate">
                    {project.title}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 truncate">
                    {project.url.replace(/^https?:\/\//, "")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setLoaded(false); setFailed(false); setReloadKey((k) => k + 1); }}
                  data-testid="preview-reload-btn"
                  title="Reload preview"
                  className="hidden sm:flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-white/70 hover:bg-white/10 transition-colors"
                >
                  <RotateCw size={15} />
                </button>
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="preview-visit-btn"
                  className="inline-flex items-center gap-1.5 rounded-full bg-white text-black text-xs font-semibold px-4 py-2 hover:bg-white/85 transition-colors"
                >
                  Visit live site <ArrowUpRight size={14} strokeWidth={2.5} />
                </a>
                <button
                  onClick={onClose}
                  data-testid="preview-close-btn"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-white/70 hover:bg-white/10 transition-colors"
                >
                  <X size={17} />
                </button>
              </div>
            </div>

            {/* iframe */}
            <div className="relative flex-1 w-full bg-[#0d0d0d]">
              {!loaded && !failed && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/40">
                  <Loader2 className="animate-spin" size={26} />
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em]">
                    Loading live preview…
                  </p>
                </div>
              )}

              {failed ? (
                <div
                  data-testid="preview-fallback"
                  className="absolute inset-0 flex flex-col items-center justify-center gap-6 px-6 text-center"
                >
                  <img
                    src={project.thumbnail}
                    alt={project.title}
                    className="max-h-[45%] w-auto rounded-xl border border-white/10 object-cover shadow-2xl"
                  />
                  <div>
                    <p className="font-[Outfit] text-lg font-semibold text-white">
                      This site can't be previewed inside the page
                    </p>
                    <p className="mt-1 text-sm text-white/50 max-w-md">
                      {project.title} blocks embedding for security. Open it in a new
                      tab to see the full live experience.
                    </p>
                  </div>
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="preview-fallback-visit-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-semibold px-6 py-3 hover:bg-white/85 transition-colors"
                  >
                    Open {project.url.replace(/^https?:\/\//, "")} <ArrowUpRight size={15} strokeWidth={2.5} />
                  </a>
                </div>
              ) : (
                <iframe
                  key={reloadKey}
                  src={project.url}
                  title={project.title}
                  onLoad={() => setLoaded(true)}
                  data-testid="preview-iframe"
                  className="h-full w-full border-0"
                  sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                />
              )}
            </div>

            {/* footer hint */}
            <div className="flex items-center gap-2 px-5 py-2 border-t border-white/10 bg-[#0A0A0A] text-white/35">
              <Monitor size={12} />
              <p className="font-mono text-[10px] tracking-wider">
                Some sites restrict embedding — use “Visit live site” for the full experience.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
